import { useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useDragResize } from '../../../hooks/useDragResize';
import { useSortedContacts } from '../../../hooks/useSorting';
import { useLayout } from '../../../context/LayoutContext';
import { useContactList } from '../../../hooks/useContactList';
import { useNavigation } from '../../../context/NavigationContext';
import { useWindowWidth } from '../../../hooks/useWindowWidth';
import SideBar from './SideBar/SideBar';
import ContactView from './ContactView/ContactView';
import './ContactsPage.css';

const ContactsPage = () => {
  // Get data from context instead of props
  const { contactList, isLoading } = useContactList();
  const { sideBarWidth, resizeSideBar, toggleMenu } = useLayout();
  const { navigateToContact } = useNavigation();
  const params = useParams();
  
  // Track window width to auto-hide sidebar below 700px
  const isWideEnoughForSidebars = useWindowWidth(700);

  // Sort contacts by name (A-Z)
  const sortedContacts = useSortedContacts(contactList);

  const currentId = useMemo(() => {
    if (!params.id) return null;
    return params.id;
  }, [params.id]);

  const contactExists = currentId && sortedContacts.some(contact => contact.id === currentId);
  const defaultId = sortedContacts[0]?.id || null;

  // Auto-navigate to first contact if needed
  useEffect(() => {
    if (!contactExists && defaultId) {
      navigateToContact(defaultId);
    }
  }, [contactExists, defaultId, navigateToContact]);

  const handleContactSelect = useCallback((contactId) => {
    navigateToContact(contactId);
  }, [navigateToContact]);

  const { onMouseDown: onResizeBarMouseDown } = useDragResize({
    onResizeX: resizeSideBar,
    getCurrentX: () => sideBarWidth,
    enableX: true,
    enableY: false,
  });

  const renderSideBar = () => {
    // Hide sidebar when screen width < 700px
    if (!isWideEnoughForSidebars) return null;

    const style = {
      display: 'flex',
      position: 'absolute',
      top: 0,
      left: 0,
      height: '100%',
      width: `${sideBarWidth}px`,
    };

    return (
      <div style={style}>
        <SideBar
          contacts={sortedContacts}
          setCurrentContactId={handleContactSelect}
          selectedContactId={currentId}
          onMenuToggle={toggleMenu}
          isLoading={isLoading}
        />
      </div>
    );
  };

  const renderMainContent = () => {
    const style = {
      display: 'flex',
      position: 'absolute',
      top: 0,
      right: 0,
      height: '100%',
      // Full width when sidebar is hidden by responsive constraint
      width: isWideEnoughForSidebars ? `calc(100% - ${sideBarWidth}px)` : '100%',
    };

    return (
      <div style={style}>
        <ContactView contactId={currentId} />
      </div>
    );
  };

  const renderResizeBar = () => {
    // Hide resize bar when sidebar is hidden
    if (!isWideEnoughForSidebars) return null;

    const style = {
      position: 'absolute',
      inset: `0px 0px 0px ${sideBarWidth}px`,
      width: '4px',
      zIndex: 1000,
      cursor: 'e-resize',
    };

    return <div style={style} onMouseDown={onResizeBarMouseDown}></div>;
  };

  return (
    <div className="contacts-page">
      { renderSideBar() }
      { renderMainContent() }
      { renderResizeBar() }
    </div>
  );
};

export default ContactsPage;
